import { PendingAntrag, FreigabeAuftrag, CockpitBehoerde, CockpitFaellig, CockpitFertig, CockpitAbgelaufen, PendingDokument } from '../../portal/ApiClient';
import { BoardCard, SpaltenId } from './types';

export interface CockpitData {
  behoerde: CockpitBehoerde[];
  faellig: CockpitFaellig[];
  fertig?: CockpitFertig[];
  abgelaufen?: CockpitAbgelaufen[];
  fristen?: Record<string, string>;   // p_id -> ZÜP-Frist (alle FZT-Personen)
}
export type Board = Record<SpaltenId, BoardCard[]>;

// Anmerkung: „Abgewiesen (FZT)" ist NUR unsere eigene Ablehnung eines
// Portal-Antrags — eine Behörden-Ablehnung (Status 10, Fälle Buchholz/
// Erdlenbruch) ist ein anderer Kanal (Watcher-Mail) und NICHT im Board.

function leeresBoard(): Board {
  return { radar: [], neu: [], in_pruefung: [], freigabe_geplant: [], behoerde: [], zuverlaessig: [], abgewiesen: [], abgelaufen: [] };
}

const key = (s: string | number | null | undefined) => (s == null ? '' : String(s));

/**
 * Baut die Kanban-Spalten aus den drei Quellen und verhindert Doppel-Anzeige:
 * Spalten werden von RECHTS nach LINKS gefüllt; eine Person landet nur in der
 * am weitesten rechts zutreffenden Spalte (Sets `belegtePid`/`belegteMgd`).
 * „Abgewiesen" nimmt bewusst NICHT an der Dedup teil (jemand darf abgewiesen
 * sein und trotzdem im Radar stehen).
 */
export function buildBoard(
  antraege: PendingAntrag[],
  auftraege: FreigabeAuftrag[],
  cockpit: CockpitData,
  dokumente: PendingDokument[] = [],
): Board {
  const board = leeresBoard();
  const belegtePid = new Set<string>();
  const belegteMgd = new Set<string>();
  const markiere = (pid?: string | number | null, mgd?: string | null) => {
    if (pid != null && key(pid)) belegtePid.add(key(pid));
    if (mgd != null && key(mgd)) belegteMgd.add(key(mgd));
  };
  const istBelegt = (pid?: string | number | null, mgd?: string | null) =>
    (pid != null && key(pid) && belegtePid.has(key(pid))) ||
    (mgd != null && key(mgd) && belegteMgd.has(key(mgd)));

  // Index: „übermittelte" Anträge (freigegeben) je p_id — werden an die
  // Behörde-/Zuverlässig-Karte angehängt (Art-Badge, ZIP), keine eigene Karte.
  const freigegebenByPid = new Map<string, PendingAntrag>();
  antraege.forEach((a) => { if (a.status === 'freigegeben' && a.p_id != null) freigegebenByPid.set(key(a.p_id), a); });
  const antragById = new Map<number, PendingAntrag>();
  antraege.forEach((a) => antragById.set(a.id, a));

  const antragCard = (a: PendingAntrag, spalte: SpaltenId): BoardCard => ({
    key: `antrag-${a.id}`, spalte, quelle: 'antrag',
    name: a.name || a.mgd_nummer, mgdNummer: a.mgd_nummer, pId: a.p_id,
    art: a.art, datum: a.created_at, datumLabel: 'eingereicht', note: a.note, antrag: a,
  });

  // Offene Portal-Anträge je Mitglied — der Cockpit-Server kennt nur
  // Behörden-/Auftrags-Vorgänge; ein eingegangener Antrag (z.B. wartet auf
  // Strafregisterauszug, Fall Näckler) zählt sonst fälschlich als
  // „ungeplant abgelaufen".
  const offenerAntragByMgd = new Map<string, PendingAntrag>();
  antraege.forEach((a) => {
    if (['neu', 'in_pruefung', 'importiert', 'freigabe_geplant'].includes(a.status)) {
      offenerAntragByMgd.set(key(a.mgd_nummer), a);
    }
  });

  // Portal-Erstanträge (jeder Status außer abgewiesen): die Person hatte nie
  // eine ZÜP, „abgelaufen" wäre falsch — die Karte heißt dann „Neuantrag"
  // (Fall Sauer, 22.09.2026). Direkt im USP angelegte Personen ohne
  // Portal-Antrag sind so nicht erkennbar und behalten das alte Label.
  const erstantragMgd = new Set<string>();
  const erstantragPid = new Set<string>();
  antraege.forEach((a) => {
    if (a.art !== 'erstantrag' || a.status === 'abgelehnt') return;
    if (key(a.mgd_nummer)) erstantragMgd.add(key(a.mgd_nummer));
    if (a.p_id != null) erstantragPid.add(key(a.p_id));
  });

  // --- Spalte 8 (ganz rechts): Abgelaufen — Crosscheck, keine Dedup.
  // Server liefert alle Personen ohne gültige ZÜP, ungeplante zuerst.
  (cockpit.abgelaufen || []).forEach((e) => {
    const offen = offenerAntragByMgd.get(key(e.mgd_nummer));
    const neuantrag = erstantragMgd.has(key(e.mgd_nummer)) || erstantragPid.has(key(e.p_id));
    board.abgelaufen.push({
      key: `abg-${e.p_id}`, spalte: 'abgelaufen', quelle: 'abgelaufen',
      name: e.name || e.mgd_nummer, mgdNummer: e.mgd_nummer, pId: e.p_id,
      datum: e.e_frist, datumLabel: 'abgelaufen am', nv: e.nv,
      neuantrag: neuantrag || undefined,
      vorgang: offen
        ? `Antrag #${offen.id} eingegangen (${offen.status === 'neu' ? 'wartet auf Unterlagen' : offen.status})`
        : e.vorgang || undefined,
      telefon: e.telefon, emailVorhanden: e.email_vorhanden,
      letzteAufforderung: e.letzte_aufforderung,
      letzterKontakt: e.letzter_kontakt,
    });
  });

  // --- Spalte 7: Abgewiesen (keine Dedup) ---
  antraege.filter((a) => a.status === 'abgelehnt').forEach((a) => board.abgewiesen.push(antragCard(a, 'abgewiesen')));

  // --- Spalte 6: Zuverlässig (letzte 7 Tage) ---
  (cockpit.fertig || []).forEach((f) => {
    if (istBelegt(f.p_id, f.mgd_nummer)) return;
    const antrag = f.p_id != null ? freigegebenByPid.get(key(f.p_id)) : undefined;
    board.zuverlaessig.push({
      key: `fertig-${f.f_id}`, spalte: 'zuverlaessig', quelle: 'fertig',
      name: f.name || f.mgd_nummer, mgdNummer: f.mgd_nummer, pId: f.p_id,
      art: antrag?.art, datum: f.abgeschlossen || f.vom, datumLabel: 'abgeschlossen',
      eFrist: f.e_frist, antrag,
    });
    markiere(f.p_id, f.mgd_nummer);
  });

  // --- Behörden-Karten: laufen in der zusammengelegten Spalte „In
  // Bearbeitung" mit (Fabian, 15.10.2026) — behalten aber spalte:'behoerde',
  // damit Badges (Resttage/Deadline) und Button-Logik unverändert bleiben.
  cockpit.behoerde.forEach((b) => {
    if (istBelegt(b.p_id, b.mgd_nummer)) return;
    const antrag = b.p_id != null ? freigegebenByPid.get(key(b.p_id)) : undefined;
    board.in_pruefung.push({
      key: `beh-${b.f_id}`, spalte: 'behoerde', quelle: 'behoerde',
      name: b.name || b.mgd_nummer, mgdNummer: b.mgd_nummer, pId: b.p_id,
      art: antrag?.art, datum: b.vom, datumLabel: 'freigegeben am',
      behoerdenStatus: b.behoerden_status, resttage: b.resttage, deadline: b.deadline,
      eFrist: b.e_frist, einklick: !antrag, antrag,
    });
    markiere(b.p_id, b.mgd_nummer);
  });

  // --- Spalte 4: Freigabe eingeplant (Aufträge geplant/wartet_admin/fehler) ---
  auftraege.filter((a) => ['geplant', 'wartet_admin', 'fehler'].includes(a.status)).forEach((a) => {
    if (istBelegt(a.p_id, a.mgd_nummer)) return;
    const antrag = a.antrag_id != null ? antragById.get(a.antrag_id) : undefined;
    board.freigabe_geplant.push({
      key: `auftrag-${a.id}`, spalte: 'freigabe_geplant', quelle: 'auftrag',
      name: a.name || antrag?.name || a.mgd_nummer, mgdNummer: a.mgd_nummer, pId: a.p_id,
      art: antrag?.art, datum: a.earliest_at, datumLabel: 'freigegeben frühestens',
      note: a.note, einklick: !antrag, fehler: a.status === 'fehler',
      wartetAdmin: a.status === 'wartet_admin', auftrag: a, antrag,
    });
    markiere(a.p_id, a.mgd_nummer);
    if (antrag) markiere(antrag.p_id, antrag.mgd_nummer);
  });
  // Randfall: Antrag 'freigabe_geplant' ohne zugehörigen Auftrag
  antraege.filter((a) => a.status === 'freigabe_geplant').forEach((a) => {
    if (istBelegt(a.p_id, a.mgd_nummer)) return;
    board.freigabe_geplant.push(antragCard(a, 'freigabe_geplant'));
    markiere(a.p_id, a.mgd_nummer);
  });

  // --- Spalte 3: In Prüfung (in_pruefung + importiert) ---
  antraege.filter((a) => ['in_pruefung', 'importiert'].includes(a.status)).forEach((a) => {
    if (istBelegt(a.p_id, a.mgd_nummer)) return;
    board.in_pruefung.push(antragCard(a, 'in_pruefung'));
    markiere(a.p_id, a.mgd_nummer);
  });

  // --- Spalte 2: Neu ---
  antraege.filter((a) => a.status === 'neu').forEach((a) => {
    if (istBelegt(a.p_id, a.mgd_nummer)) return;
    board.neu.push(antragCard(a, 'neu'));
    markiere(a.p_id, a.mgd_nummer);
  });
  // Nachgereichte Dokumente (Prüf-Queue): eigene Karten, KEINE Dedup — das
  // Dokument braucht seine eigene Aktion, auch wenn die Person schon eine
  // Antrags-Karte hat (Fall Demetz 30.08.: Queue war ohne Karte unsichtbar).
  dokumente.filter((d) => d.status === 'neu').forEach((d) => {
    board.neu.push({
      key: `dok-${d.id}`, spalte: 'neu', quelle: 'dokument',
      name: d.name || `Mitglied ${d.mgd_nummer}`, mgdNummer: d.mgd_nummer, pId: d.p_id,
      datum: d.uploaded_at, datumLabel: 'hochgeladen', note: d.note, dokument: d,
    });
  });

  // --- Spalte 1: Radar (grün = läuft schon -> raus; sonst dedup) ---
  (cockpit.faellig || []).forEach((f) => {
    if (f.ampel === 'gruen') return;
    if (istBelegt(f.p_id, f.mgd_nummer)) return;
    board.radar.push({
      key: `radar-${f.p_id}`, spalte: 'radar', quelle: 'radar',
      name: f.name || f.mgd_nummer, mgdNummer: f.mgd_nummer, pId: f.p_id,
      datum: f.e_frist, datumLabel: 'läuft ab', ampel: f.ampel, vorgang: f.vorgang,
      telefon: f.telefon, brauchtSr: f.braucht_sr, srLaender: f.sr_laender,
    });
    markiere(f.p_id, f.mgd_nummer);
  });

  // Sortierung je Spalte
  board.radar.sort((a, b) => key(a.datum).localeCompare(key(b.datum)));
  board.neu.sort((a, b) => key(a.datum).localeCompare(key(b.datum)));
  board.in_pruefung.sort((a, b) => key(a.datum).localeCompare(key(b.datum)));
  board.freigabe_geplant.sort((a, b) => key(a.datum).localeCompare(key(b.datum)));
  board.behoerde.sort((a, b) => key(a.deadline).localeCompare(key(b.deadline)));
  board.zuverlaessig.sort((a, b) => key(b.datum).localeCompare(key(a.datum)));
  board.abgewiesen.sort((a, b) => key(b.datum).localeCompare(key(a.datum)));
  // ZÜP-Frist auf JEDE Karte mit p_id (Fabian 23.09.2026) — Spalten, die
  // die Frist schon vom Server tragen (Behörde/Zuverlässig), behalten sie.
  const fristen = cockpit.fristen || {};
  (Object.values(board) as BoardCard[][]).forEach((spalte) => spalte.forEach((c) => {
    if (!c.eFrist && c.pId != null && fristen[key(c.pId)]) c.eFrist = fristen[key(c.pId)];
  }));

  return board;
}
